/**
 * commandSuggester — pick the right slash-command suggestion given
 * either (a) a state transition (Bundle B) or (b) a writer's
 * free-text intent (Bundle D). Same registry, same return shape,
 * same renderer — one mechanism for both triggers.
 *
 * Spec 9.5 (Bundle B+D, 2026-04-27).
 *
 * SRP: pure functions over the COMMAND_REGISTRY. No React, no
 * dispatch, no I/O. Caller (VariantCShell) decides whether to
 * emit the resulting ``agent_note`` event into the chat.
 *
 * # DECISION: the suggester is generic — adding a new command
 * #   never touches this file.
 * # WHY: per the user's "no hardcoded workflow" rule. Each command
 * #      declares its own ``suggestAfter`` predicate and
 * #      ``intentKeywords`` in the registry; the suggester just
 * #      walks the registry and returns the first match. Replacing
 * #      this MVP heuristic with a backend LLM tool call later
 * #      changes ONLY this file (the registry stays as the agent's
 * #      tool manifest).
 * # DATE: 2026-04-27
 *
 * # DECISION: ``formatSuggestionText`` is the single renderer.
 * # WHY: both Bundle B (state-transition trigger) and Bundle D
 * #      (intent trigger) emit the same kind of agent_note. Sharing
 * #      the renderer means the writer sees consistent copy
 * #      regardless of how the suggestion was triggered.
 * # DATE: 2026-04-27
 */

import { COMMAND_REGISTRY, type Command, type CommandSnapshot } from "./commandRegistry";

/** What the suggester returns: the picked command + a reason tag
 *  for telemetry / future debugging. Pure — caller decides what
 *  to do with it. */
export interface Suggestion {
  /** The matched ``Command.id``. The full Command object is
   *  obtainable via ``findCommandById``. */
  commandId: string;
  /** Why we surfaced it. Lets the chat note distinguish
   *  state-transition guidance from intent-routing guidance, and
   *  lets future analytics partition the two. */
  source: "post-action" | "intent";
}

// ---------------------------------------------------------------------------
// Bundle B — post-action / state-transition trigger
// ---------------------------------------------------------------------------

/**
 * Walks the registry in declared order and returns the first
 * command whose ``suggestAfter`` predicate fires for the snapshot.
 * Returns null when nothing applies.
 *
 * Pure; same input → same output. Order in the registry IS the
 * priority order — see commandRegistry's note on placing
 * flow-completers before flow-preparers.
 */
export function suggestPostAction(
  state: CommandSnapshot,
  registry: ReadonlyArray<Command> = COMMAND_REGISTRY,
): Suggestion | null {
  for (const c of registry) {
    if (c.suggestAfter?.(state)) {
      return { commandId: c.id, source: "post-action" };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Bundle D — free-text intent trigger
// ---------------------------------------------------------------------------

/**
 * Maximum length (chars) and word count for text that the
 * suggester will consider. Above either threshold we treat the
 * text as a genuine chat prompt and fall through to the backend
 * unconditionally.
 *
 * # DECISION (2026-04-30): length-gate the intent suggester.
 * # WHY: a writer typed "Draft a table on all the components of
 * #      the medical device and their key details in common
 * #      columns of parameters" pre-draft. The substring "draft"
 * #      matched the /draft command's intentKeywords → the
 * #      suggester intercepted → the dedup guard in
 * #      emitSuggestionEvent silently swallowed the message
 * #      because an earlier post-action note already mentioned
 * #      /draft. Net effect: the chat API was never called and
 * #      the writer saw their prompt with no response. Long
 * #      sentence-shaped messages are real chat prompts, not
 * #      hint-fishing — gate the suggester so they bypass it.
 * #      ~60 chars / ~8 words covers "can i draft a csr",
 * #      "library please", "start writing my report", etc.
 */
const SUGGESTER_MAX_CHARS = 60;
const SUGGESTER_MAX_WORDS = 8;

/**
 * Match the writer's free-text against each command's
 * ``intentKeywords``. Returns the FIRST registry-order match.
 * Returns null when the text is empty, looks like a slash command
 * (the rail handles those directly), exceeds the length gate
 * (treat as a real chat prompt), or no keyword matches.
 *
 * Pure word-boundary match — no fuzzy / stemming / regex on the
 * caller's text. The MVP local heuristic; replace with a backend
 * LLM tool call when the agentic version lands. The registry
 * doesn't change.
 *
 * Skip-when-already-actionable rule: if the suggested command's
 * ``suggestAfter`` is also true for the current state, the writer
 * just needs the nudge — surface the intent suggestion. If
 * ``suggestAfter`` is FALSE (meaning the command isn't actionable
 * yet, e.g. /draft without a library), still surface it because
 * the writer's text PROVED their intent — the suggestion now
 * doubles as guidance about the missing prerequisite. The chat
 * copy is the same; the writer reads it and acts.
 */
export function suggestFromText(
  text: string,
  _state: CommandSnapshot,
  registry: ReadonlyArray<Command> = COMMAND_REGISTRY,
): Suggestion | null {
  const cleaned = text.trim().toLowerCase();
  if (cleaned.length === 0) return null;
  // Slash commands are routed by the rail; the suggester only
  // helps with non-slash free text.
  if (cleaned.startsWith("/")) return null;
  // Length gate — long, sentence-shaped messages are real chat
  // prompts, never hint-fishing. Bypass the suggester so the
  // backend gets a chance to respond. See SUGGESTER_MAX_CHARS
  // for the rationale + the bug it fixes.
  if (cleaned.length > SUGGESTER_MAX_CHARS) return null;
  // Word count is a second safety net — a short string of long
  // words ("biocompatibility cytotoxicity") shouldn't slip past
  // the char gate, and a long string of short words shouldn't
  // either. Both are hint-shaped, neither should match.
  const wordCount = cleaned.split(/\s+/).filter((w) => w.length > 0).length;
  if (wordCount > SUGGESTER_MAX_WORDS) return null;

  for (const c of registry) {
    if (matchesAnyKeyword(cleaned, c.intentKeywords)) {
      return { commandId: c.id, source: "intent" };
    }
  }
  return null;
}

/** Pure substring match against any keyword. Caller passes the
 *  already-lower-cased cleaned text; this scans the keyword list.
 *  Kept tiny so the matching strategy is one place to evolve
 *  (word boundary, regex, embedding lookup, LLM tool call) without
 *  touching the iterators above. */
function matchesAnyKeyword(cleanedText: string, keywords: ReadonlyArray<string>): boolean {
  for (const kw of keywords) {
    if (cleanedText.includes(kw.toLowerCase())) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Bundle D — LLM-backed intent classifier
// ---------------------------------------------------------------------------

/**
 * Server-side intent classifier — POSTs the writer's text + the
 * command manifest to ``/api/intent-classify`` and gets back a
 * ``commandId | null`` decision from a Haiku call.
 *
 * # DECISION (2026-04-30): make the LLM path the default;
 * #   ``suggestFromText`` (keyword) becomes the offline / timeout
 * #   fallback.
 * # WHY: the keyword matcher has well-known failure modes — substring
 * #      collisions ("ib" inside "library"), fixed gates that catch
 * #      false positives but not false negatives, every new command
 * #      pulls more keyword tuning. Haiku reads ``intentDescription``
 * #      and reasons about meaning — adding a command means writing
 * #      one sentence, no keyword tuning.
 * # HOW TO APPLY: callers should ``Promise.race`` this against a
 * #      timeout that triggers the keyword fallback. The frontend
 * #      never blocks on the LLM longer than the racing timeout.
 *
 * Returns null when:
 *   * the text is empty / slash-prefixed / over the local length gate,
 *   * the Haiku route returns ``commandId: null`` (no match, too_long,
 *     llm_error, invalid_id, empty — every null reason is treated the
 *     same: fall through to the chat backend),
 *   * confidence is "low" — guard against confident-wrong canned hints.
 *
 * Pure-ish: the only side effect is the network call. Caller passes
 * an AbortSignal so a stale racing call can be cancelled.
 */
export async function suggestFromTextLLM(
  text: string,
  _state: CommandSnapshot,
  options: {
    /** AbortSignal so the caller can cancel a losing race. */
    signal?: AbortSignal;
    /** Override the API base — tests / non-default deployments. */
    apiBase?: string;
    /** Override the registry — tests pin a synthetic manifest. */
    registry?: ReadonlyArray<Command>;
  } = {},
): Promise<Suggestion | null> {
  const cleaned = text.trim();
  if (cleaned.length === 0) return null;
  // Slash commands routed by the rail — never classified.
  if (cleaned.startsWith("/")) return null;
  // Same gate as the keyword path: substantive prompts skip the
  // classifier entirely. Mirrors the server's own gate so this is
  // belt-and-suspenders — neither side wastes a round trip.
  if (cleaned.length > SUGGESTER_MAX_CHARS) return null;
  const wordCount = cleaned.split(/\s+/).filter((w) => w.length > 0).length;
  if (wordCount > SUGGESTER_MAX_WORDS) return null;

  const registry = options.registry ?? COMMAND_REGISTRY;
  const apiBase = options.apiBase ?? "/api";

  const manifest = registry.map((c) => ({
    id: c.id,
    syntax: c.syntax,
    description: c.intentDescription,
  }));

  let resp: Response;
  try {
    resp = await fetch(`${apiBase}/intent-classify`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: cleaned, commands: manifest }),
      signal: options.signal,
    });
  } catch {
    // Network error / aborted race → fall through to keyword path.
    return null;
  }

  if (!resp.ok) return null;

  let data: {
    commandId: string | null;
    confidence?: "high" | "low";
    reason?: string;
  };
  try {
    data = await resp.json();
  } catch {
    return null;
  }

  if (!data.commandId) return null;
  // Low-confidence picks fall through to chat — better a real answer
  // than a confident-wrong canned hint.
  if (data.confidence === "low") return null;
  // Defensive — server already validates id, but the registry the
  // server saw came from the client; double-check it resolves locally.
  const exists = registry.some((c) => c.id === data.commandId);
  if (!exists) return null;

  return { commandId: data.commandId, source: "intent" };
}

/**
 * Race the LLM classifier against a deadline; on miss, fall back to
 * the local keyword matcher. Returns the same Suggestion shape so
 * the caller doesn't care which path won.
 *
 * # DECISION: race, don't sequence.
 * # WHY: blocking the writer's send on a Haiku round-trip even once
 * #      adds perceptible latency. The race resolves on whichever
 * #      finishes first: a fast Haiku call wins (most cases), a slow
 * #      one loses to the synchronous keyword match. Either way the
 * #      writer's send is dispatched within the race deadline.
 *
 * Default deadline: 700ms. Tuned for Haiku 4.5 p95 plus a small
 * margin; tunable per-call for tests.
 */
export async function suggestFromTextWithLLM(
  text: string,
  state: CommandSnapshot,
  options: {
    apiBase?: string;
    registry?: ReadonlyArray<Command>;
    /** Race deadline in ms. After this elapses we use the keyword
     *  fallback regardless of whether the LLM has answered. */
    deadlineMs?: number;
  } = {},
): Promise<Suggestion | null> {
  const deadline = options.deadlineMs ?? 700;
  const registry = options.registry ?? COMMAND_REGISTRY;

  // Both paths apply the same length / slash gates internally, so a
  // long sentence-shaped prompt never reaches either.
  const controller = new AbortController();
  const llmPromise = suggestFromTextLLM(text, state, {
    signal: controller.signal,
    apiBase: options.apiBase,
    registry,
  });

  const timeoutPromise = new Promise<"timeout">((resolve) => {
    setTimeout(() => resolve("timeout"), deadline);
  });

  const winner = await Promise.race([llmPromise, timeoutPromise]);
  if (winner === "timeout") {
    controller.abort();
    return suggestFromText(text, state, registry);
  }
  if (winner !== null) return winner;
  // LLM resolved with null (no match / low confidence / error). The
  // local matcher might still pick a canned hint — try it as a
  // last-line fallback for the offline / pre-Haiku-rollout case.
  // # DECISION: when the LLM is the source of truth (it answered),
  // # respect its null and DON'T retry with keywords. Keyword retry
  // # only fires when the LLM never answered (timeout / network).
  return null;
}

// ---------------------------------------------------------------------------
// Render — shared between both triggers
// ---------------------------------------------------------------------------

/**
 * Build the assistant prose for a Suggestion. Pure; exported so
 * tests pin the exact wording without driving a React tree.
 *
 * Each command owns its own intent + post-action copy in
 * ``command.guidance`` — see CommandRegistry for why. This renderer
 * is intentionally trivial (pick the right field) so guidance
 * voice changes never require touching the renderer.
 */
export function formatSuggestionText(suggestion: Suggestion, command: Command): string {
  return suggestion.source === "post-action" ? command.guidance.postAction : command.guidance.intent;
}
